import { api } from '../config/api';

export interface AIRule {
  id: number;
  name: string;
  description?: string;
  pattern: string;
  pattern_type: 'exact' | 'contains' | 'regex' | 'starts_with' | 'ends_with';
  case_sensitive: boolean;
  actions: {
    property_id?: number;
    tags?: string[];
    document_type?: string;
    vendor?: string;
    amount?: number;
  };
  scope: 'queue_only' | 'all_documents';
  active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface RuleCreate {
  name: string;
  description?: string;
  pattern: string;
  pattern_type: 'exact' | 'contains' | 'regex' | 'starts_with' | 'ends_with';
  case_sensitive: boolean;
  actions: {
    property_id?: number;
    tags?: string[];
    document_type?: string;
  };
  scope: 'queue_only' | 'all_documents';
  active: boolean;
  priority: number;
}

export interface RuleTestRequest {
  text: string;
  rule_ids?: number[];
}

export interface RuleTestResult {
  rule_id: number;
  rule_name: string;
  matched: boolean;
  actions_to_apply?: any;
}

export interface RuleExecutionResponse {
  id: number;
  rule_id: number;
  document_id: string;
  matched: boolean;
  actions_applied?: any;
  executed_at: string;
  error_message?: string;
}

class AIRulesService {
  async getRules(activeOnly = false): Promise<AIRule[]> {
    const params = activeOnly ? { active_only: true } : {};
    const response = await api.get('/ai-rules/', { params });
    return response.data;
  }

  async getRule(id: number): Promise<AIRule> {
    const response = await api.get(`/ai-rules/${id}`);
    return response.data;
  }

  async createRule(rule: RuleCreate): Promise<AIRule> {
    console.log('Creating rule with data:', rule);
    try {
      const response = await api.post('/ai-rules/', rule);
      console.log('Rule created successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Failed to create rule:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  }

  async updateRule(id: number, updates: Partial<RuleCreate>): Promise<AIRule> {
    const response = await api.patch(`/ai-rules/${id}`, updates);
    return response.data;
  }

  async deleteRule(id: number): Promise<void> {
    await api.delete(`/ai-rules/${id}`);
  }

  async testRules(request: RuleTestRequest): Promise<RuleTestResult[]> {
    const response = await api.post('/ai-rules/test', request);
    return response.data;
  }

  async getRuleExecutions(ruleId: number, skip = 0, limit = 100): Promise<RuleExecutionResponse[]> {
    const response = await api.get(`/ai-rules/${ruleId}/executions`, {
      params: { skip, limit }
    });
    return response.data;
  }

  // Document rule application methods
  async applyRulesToDocument(documentId: string, ruleIds?: number[]): Promise<any> {
    try {
      const response = await api.post(`/documents/${documentId}/apply-rules`, {
        rule_ids: ruleIds
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 501) {
        throw new Error('Rule application is temporarily disabled. You can still create and manage rules.');
      }
      throw error;
    }
  }

  async applyRulesToDocuments(documentIds: string[], ruleIds?: number[]): Promise<any> {
    try {
      const response = await api.post('/documents/apply-rules/batch', {
        document_ids: documentIds,
        rule_ids: ruleIds
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 501) {
        throw new Error('Rule application is temporarily disabled. You can still create and manage rules.');
      }
      throw error;
    }
  }

  async applyRulesToProperty(propertyId: number, ruleIds?: number[]): Promise<any> {
    const response = await api.post(`/documents/apply-rules/property/${propertyId}`, {
      rule_ids: ruleIds
    });
    return response.data;
  }

  async applyRulesToTags(tagIds: number[], ruleIds?: number[]): Promise<any> {
    const response = await api.post('/documents/apply-rules/tags', {
      tag_ids: tagIds,
      rule_ids: ruleIds
    });
    return response.data;
  }

  async applyRulesToDocumentType(documentType: string, ruleIds?: number[]): Promise<any> {
    const response = await api.post(`/documents/apply-rules/type/${documentType}`, {
      rule_ids: ruleIds
    });
    return response.data;
  }
}

export const aiRulesService = new AIRulesService();